import { useMemo } from 'react';
import Plot from 'react-plotly.js';
import type { Data, Layout } from 'plotly.js';
import type { TestChartData } from '@/lib/impedance';
import { radToDeg } from '@/lib/impedance';
import { generateTemporalPalette } from './useChartData';

// Fixed per-channel palette: 4 slots spanning blue → red, same function as temporal palette.
// CH1=hsl(240), CH2=hsl(160), CH3=hsl(80), CH4=hsl(0)  matches the attached reference image.
const CHANNEL_IDS = ['CH1', 'CH2', 'CH3', 'CH4'];
const CHANNEL_PALETTE = generateTemporalPalette(4);
export const CHANNEL_COLOR_MAP = new Map(CHANNEL_IDS.map((ch, i) => [ch, CHANNEL_PALETTE[i]]));

export interface CombinedPlotProps {
  /** Must contain exactly one test; callers are responsible for enforcement. */
  testData: TestChartData[];
  visibleChannels: Set<string>;
  phaseUnit: 'rad' | 'deg';
  yScale: 'linear' | 'log';
  onPlotRef?: (el: HTMLElement | null) => void;
}

export function CombinedPlot({
  testData,
  visibleChannels,
  phaseUnit,
  yScale,
  onPlotRef,
}: CombinedPlotProps) {
  const test = testData[0];

  const traces = useMemo<Data[]>(() => {
    if (!test) return [];
    const result: Data[] = [];

    for (const mtype of ['baseline', 'specimen'] as const) {
      const measurementData = mtype === 'baseline' ? test.baseline : test.specimen;
      if (!measurementData) continue;

      for (const channel of measurementData.channels) {
        if (!visibleChannels.has(channel.channelId)) continue;

        const color = CHANNEL_COLOR_MAP.get(channel.channelId) ?? '#888';
        const isBaseline = mtype === 'baseline';
        const dash = isBaseline ? 'solid' : 'dash';
        const suffix = isBaseline ? 'BL' : 'SP';
        const name = `${channel.channelId} ${suffix}`;

        // Only show legend entry on first trace for this channel (baseline)
        const isFirstForChannel = isBaseline;

        // ── Nyquist trace  (xaxis='x', yaxis='y') ────────────────────────
        result.push({
          x: channel.points.map((p) => p.zReal),
          y: channel.points.map((p) => -p.zImag),
          customdata: channel.points.map((p) => p.frequency),
          type: 'scatter',
          mode: 'lines+markers',
          name,
          xaxis: 'x',
          yaxis: 'y',
          line: { color, dash, width: 1.5 },
          marker: { size: 4, color },
          legendgroup: channel.channelId,
          legendgrouptitle: isFirstForChannel ? { text: channel.channelId } : undefined,
          showlegend: true,
          hovertemplate:
            `<b>${name}</b><br>` +
            `Z_real: %{x:.1f} Ω<br>` +
            `-Z_imag: %{y:.1f} Ω<br>` +
            `Freq: %{customdata:.2f} Hz<br>` +
            `<extra></extra>`,
        } as Data);

        // ── Magnitude trace  (xaxis='x3', yaxis='y2') ───────────────────
        result.push({
          x: channel.points.map((p) => p.frequency),
          y: channel.points.map((p) => p.magnitude),
          type: 'scatter',
          mode: 'lines+markers',
          name,
          xaxis: 'x3',
          yaxis: 'y2',
          line: { color, dash, width: 1.5 },
          marker: { size: 3, color },
          legendgroup: channel.channelId,
          showlegend: false,
          hovertemplate:
            `<b>${name}</b><br>` +
            `Freq: %{x:.2f} Hz<br>` +
            `|Z|: %{y:.1f} Ω<br>` +
            `<extra></extra>`,
        } as Data);

        // ── Phase trace  (xaxis='x2', yaxis='y3') ───────────────────────
        const phaseValues =
          phaseUnit === 'deg'
            ? channel.points.map((p) => radToDeg(p.phase))
            : channel.points.map((p) => p.phase);

        result.push({
          x: channel.points.map((p) => p.frequency),
          y: phaseValues,
          type: 'scatter',
          mode: 'lines',
          name,
          xaxis: 'x2',
          yaxis: 'y3',
          line: { color, dash, width: 1.5 },
          legendgroup: channel.channelId,
          showlegend: false,
          hovertemplate:
            `<b>${name}</b><br>` +
            `Freq: %{x:.2f} Hz<br>` +
            `Phase: %{y:.3f} ${phaseUnit === 'deg' ? '°' : 'rad'}<br>` +
            `<extra></extra>`,
        } as Data);
      }
    }

    return result;
  }, [test, visibleChannels, phaseUnit]);

  const layout = useMemo<Partial<Layout>>(
    () =>
      ({
        // ── Nyquist: left ~70% ────────────────────────────────────────────
        xaxis: {
          title: { text: 'Real (Ω)' },
          domain: [0, 0.68],
          scaleanchor: 'y',
          scaleratio: 1,
          autorange: true,
          anchor: 'y',
        },
        yaxis: {
          title: { text: '-Z_imag (Ω)' },
          domain: [0, 1.0],
          autorange: true,
          anchor: 'x',
        },
        // ── Bode: phase x-axis (bottom, has tick labels) ─────────────────
        xaxis2: {
          domain: [0.73, 1.0],
          type: 'log',
          anchor: 'y3',
          title: { text: 'Frequency (Hz)' },
          autorange: true,
        },
        // ── Bode: magnitude x-axis (top, shares range with x2) ───────────
        xaxis3: {
          domain: [0.73, 1.0],
          type: 'log',
          anchor: 'y2',
          matches: 'x2',
          showticklabels: false,
          autorange: true,
        },
        // ── Magnitude panel (top right) ───────────────────────────────────
        yaxis2: {
          title: { text: '|Z| (Ω)' },
          type: yScale,
          domain: [0.52, 1.0],
          anchor: 'x3',
          autorange: true,
        },
        // ── Phase panel (bottom right) ────────────────────────────────────
        yaxis3: {
          title: { text: `Phase (${phaseUnit === 'deg' ? '°' : 'rad'})` },
          domain: [0.0, 0.48],
          anchor: 'x2',
          autorange: true,
        },
        legend: {
          orientation: 'h',
          y: -0.12,
          x: 0,
          groupclick: 'toggleitem',
        },
        hovermode: 'closest',
        margin: { t: 30, r: 20, b: 100, l: 60 },
        autosize: true,
      }) as Partial<Layout>,
    [yScale, phaseUnit],
  );

  if (!test) return null;

  return (
    <Plot
      data={traces}
      layout={layout}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler
      config={{ responsive: true, displayModeBar: true, displaylogo: false, scrollZoom: true }}
      onInitialized={(_, graphDiv) => onPlotRef?.(graphDiv)}
      onPurge={() => onPlotRef?.(null)}
    />
  );
}
